import { useEffect, useState } from "react";
import { Share } from "lucide-react";

import { useI18n } from "../../i18n";
import usePlatform, { PLATFORM_NAMES } from "../../platform/usePlatform";
import { usePwaInstallPrompt } from "../../pwa";

const isIOSUA = /iphone|ipad|ipod/i.test(navigator.userAgent);
const isAndroidUA = /android/i.test(navigator.userAgent);
const isSamsungBrowserUA = /samsungbrowser/i.test(navigator.userAgent);
const isXiaomiBrowserUA = /miuibrowser|xiaomi|redmi|poco/i.test(navigator.userAgent);

export default function PwaInstallGlobalPrompt() {
  const { t } = useI18n();
  const platform = usePlatform();
  const { canPromptInstall, isInstalled: isPwaInstalled, promptInstall } = usePwaInstallPrompt();
  const isDesktopPlatform = platform === PLATFORM_NAMES.desktop;

  const [showIosPrompt, setShowIosPrompt] = useState(isIOSUA);
  const [showAndroidManualPrompt, setShowAndroidManualPrompt] = useState(false);
  const showInstallPrompt = canPromptInstall && !isPwaInstalled;
  const isAndroidCandidate = !isIOSUA && isAndroidUA;

  useEffect(() => {
    if (isDesktopPlatform || !isPwaInstalled) return;
    setShowIosPrompt(false);
    setShowAndroidManualPrompt(false);
  }, [isDesktopPlatform, isPwaInstalled]);

  useEffect(() => {
    if (isDesktopPlatform || !isAndroidCandidate || isPwaInstalled) return;
    if (showInstallPrompt) {
      setShowAndroidManualPrompt(false);
      return;
    }

    const timeout = setTimeout(() => {
      setShowAndroidManualPrompt(true);
    }, 8000);

    return () => clearTimeout(timeout);
  }, [isAndroidCandidate, isDesktopPlatform, isPwaInstalled, showInstallPrompt]);

  const handleInstallClick = async () => {
    const choice = await promptInstall();
    if (choice?.outcome !== "accepted" && isAndroidCandidate && !isPwaInstalled) {
      setShowAndroidManualPrompt(true);
    }
  };

  let androidInstallHintKey = "home.androidInstallHintGeneric";
  if (isSamsungBrowserUA) androidInstallHintKey = "home.androidInstallHintSamsung";
  else if (isXiaomiBrowserUA) androidInstallHintKey = "home.androidInstallHintXiaomi";

  if (isDesktopPlatform) return null;

  const showIosHint = isIOSUA && !isPwaInstalled && showIosPrompt;
  const showAndroidHint = isAndroidCandidate && !isPwaInstalled && !showInstallPrompt && showAndroidManualPrompt;
  if (!showInstallPrompt && !showIosHint && !showAndroidHint) return null;

  return (
    <>
      {showInstallPrompt && (
        <div className="fixed bottom-6 inset-x-4 z-50 bg-white border shadow-md rounded-xl p-4 flex items-center justify-between">
          <span className="text-sm text-gray-800">{t("home.installPrompt")}</span>
          <button
            onClick={handleInstallClick}
            className="ml-4 rounded-full bg-[#c6283f] px-4 py-1.5 text-sm font-semibold text-white"
          >
            {t("home.install")}
          </button>
        </div>
      )}

      {showAndroidHint && (
        <div className="fixed bottom-6 inset-x-4 z-50 bg-white border shadow-md rounded-xl p-4 flex items-start gap-3">
          <div className="text-sm text-gray-800">
            <div className="mb-1 text-base font-semibold">{t("home.androidInstallTitle")}</div>
            <p>{t(androidInstallHintKey)}</p>
          </div>
          <button
            onClick={() => setShowAndroidManualPrompt(false)}
            className="ml-auto px-3 py-1 text-gray-400 hover:text-gray-700"
            aria-label={t("home.close")}
          >
            ✕
          </button>
        </div>
      )}

      {showIosHint && (
        <div className="fixed bottom-6 inset-x-4 z-50 bg-white border shadow-md rounded-xl p-4 flex items-start gap-3">
          <div className="text-sm text-gray-800">
            <div className="mb-1 text-base font-semibold">{t("home.iosInstallTitle")}</div>
            <p>
              {t("home.iosInstallStep1")} <strong className="mx-1">{t("home.iosInstallShare")}</strong>
              <Share className="mx-1 inline h-4 w-4 align-text-bottom text-gray-700" />
              {t("home.iosInstallStep2")} <strong className="mx-1">{t("home.iosInstallHomeScreen")}</strong>.
            </p>
          </div>
          <button
            onClick={() => setShowIosPrompt(false)}
            className="ml-auto px-3 py-1 text-gray-400 hover:text-gray-700"
            aria-label={t("home.close")}
          >
            ✕
          </button>
        </div>
      )}
    </>
  );
}
